/**
 * ModuleAccessContext — publishes the shell's single, fail-closed entitlement
 * gate to every surface rendered beneath the AppShell (browse catalogue,
 * command palette, module host, deep-link redirects, widget canvas).
 *
 * AppShell computes the gate once (manifest catalogue ∩ evaluator-allowed set ∩
 * nav-mode workspace) and provides it here, so no surface re-derives its own
 * notion of "which modules may this principal see" — the defect this closes was
 * precisely that HomePage computed one set and then rendered a different one.
 *
 * The default value when no provider is mounted is deliberately the CLOSED one
 * (`loading`, empty set): a surface accidentally rendered outside the shell
 * shows nothing rather than the whole catalogue.
 */
import * as React from 'react';
import {
  isModulePermitted,
  type ModuleAccess,
} from './entitlementGate';

const CLOSED: ModuleAccess = { state: 'loading', permittedModuleIds: new Set<string>() };

const ModuleAccessContext = React.createContext<ModuleAccess>(CLOSED);

export function ModuleAccessProvider({
  access,
  children,
}: {
  access: ModuleAccess;
  children: React.ReactNode;
}) {
  return (
    <ModuleAccessContext.Provider value={access}>{children}</ModuleAccessContext.Provider>
  );
}

/** Read the shell's entitlement gate. Fail-closed when unprovided. */
export function useModuleAccess(): ModuleAccess {
  return React.useContext(ModuleAccessContext);
}

/** Convenience predicate for a single module id. */
export function useIsModulePermitted(moduleId: string | null | undefined): boolean {
  const access = useModuleAccess();
  return isModulePermitted(access, moduleId);
}
